// FILE: ManageSkillsTab.js

import React, { useEffect, useMemo, useState } from "react";

import {
    Box,
    Typography,
    Grid,
    TextField,
    MenuItem,
    Button,
    Paper,
    Divider,
    Select,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    Chip,
    FormControlLabel,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    Collapse,
    CircularProgress,
} from "@mui/material";

import {
    Psychology as PsychologyIcon,
    CheckCircle as CheckCircleIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Close as CloseIcon,
    ExpandLess,
    ExpandMore,
    AutoAwesome as AutoAwesomeIcon,
} from "@mui/icons-material";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import api from "../axios/axiosInstance";
import PromptHelpDialog from "../dialogs/PromptHelpDialog";
import { useTheme } from "@mui/material/styles";
import AIGenerationLimitDialog from "../dialogs/AIGenerationLimitDialog";
import handleAIError from "../utils/handleAIError";
import useSanitizationGuard from "../hooks/useSanitizationGuard";


// Matches the DOK accent palette used on the single-student
// assessment page (UnitLevelProgress.js), so a DOK level reads
// the same way everywhere in the app.
const DOK_LEVEL_STYLES = {
    1: {
        color: "#2e7d32",
        background: "#f1f8e9",
        border: "#a5d6a7",
    },
    2: {
        color: "#1565c0",
        background: "#e3f2fd",
        border: "#90caf9",
    },
    3: {
        color: "#6a1b9a",
        background: "#f3e5f5",
        border: "#ce93d8",
    },
    4: {
        color: "#795548",
        background: "#efebe9",
        border: "#bcaaa4",
    },
};


const ManageSkillsTab = ({ setActiveTabGlobal }) => {
    const theme = useTheme();

    // =========================================================
    // CORE DATA
    // =========================================================

    const [subjects, setSubjects] = useState([]);
    const [units, setUnits] = useState([]);

    const [selectedSubject, setSelectedSubject] = useState("");
    const [selectedUnit, setSelectedUnit] = useState("");

    const [dokLevels, setDokLevels] = useState([]);
    const [savedSkills, setSavedSkills] = useState([]);

    // =========================================================
    // SAVED SKILL UI
    // =========================================================

    const [selectedSavedSkillIds, setSelectedSavedSkillIds] = useState([]);

    const [expandedDok, setExpandedDok] = useState({
        1: true,
        2: false,
        3: false,
        4: false,
    });

    const [editingSkillId, setEditingSkillId] = useState(null);
    const [editingSkillText, setEditingSkillText] = useState("");

    const [savingSkillId, setSavingSkillId] = useState(null);

    const [addingSkillDok, setAddingSkillDok] = useState(null);
    const [newSkillDescription, setNewSkillDescription] = useState("");
    const [addingSkill, setAddingSkill] = useState(false);

    // =========================================================
    // AI GENERATOR
    // =========================================================

    const [aiDialogOpen, setAiDialogOpen] = useState(false);
    const [topicPrompt, setTopicPrompt] = useState("");

    const [generatedSkills, setGeneratedSkills] = useState([]);
    const [activeAiDok, setActiveAiDok] = useState(1);

    const [generating, setGenerating] = useState(false);
    const [regeneratingDok, setRegeneratingDok] = useState(null);
    const [savingGenerated, setSavingGenerated] = useState(false);

    const [editingGeneratedId, setEditingGeneratedId] = useState(null);
    const [editingGeneratedText, setEditingGeneratedText] = useState("");

    // =========================================================
    // DIALOGS
    // =========================================================

    const [successDialog, setSuccessDialog] = useState({
        open: false,
        title: "",
        message: "",
    });

    const [errorDialog, setErrorDialog] = useState({
        open: false,
        message: "",
    });

    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        type: null,
        skillId: null,
    });

    const [showLimitDialog, setShowLimitDialog] = useState(false);

    // =========================================================
    // SANITIZATION
    // =========================================================

    const {
        checkFreeText,
        WarningDialog,
    } = useSanitizationGuard();


    // =========================================================
    // PROMPT HELP
    // =========================================================

    const helperForSkills = (
        <>
            <Typography gutterBottom>
                Enter a clear topic, concept, or learning objective so the AI
                can generate specific learning targets.
            </Typography>

            <Typography variant="subtitle2" fontWeight="bold">
                Examples:
            </Typography>

            <Box sx={{ pl: 2, mt: 1, mb: 2 }}>
                <Typography variant="body2">
                    • Fractions for Grade 5 students
                </Typography>

                <Typography variant="body2">
                    • Understanding ecosystems with a climate focus
                </Typography>

                <Typography variant="body2">
                    • Multiplication for Grade 1 using equal groups
                </Typography>

                <Typography variant="body2">
                    • Indigenous perspectives in Canadian History
                </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
                Avoid very broad prompts such as "Math" or "Grade 6".
            </Typography>
        </>
    );


    // =========================================================
    // LOAD SUBJECTS
    // =========================================================

    useEffect(() => {
        api.get(
            `${process.env.REACT_APP_BACKEND_URL}/grades/subjects/`,
            {
                withCredentials: true,
            }
        )
            .then((res) => {
                setSubjects(res.data || []);
            })
            .catch((err) => {
                console.error("Failed to fetch subjects:", err);
            });
    }, []);


    // =========================================================
    // LOAD UNITS
    // =========================================================

    useEffect(() => {
        if (!selectedSubject) {
            setUnits([]);
            return;
        }

        api.get(
            `${process.env.REACT_APP_BACKEND_URL}/grades/units/`,
            {
                params: {
                    subject_id: selectedSubject,
                },
                withCredentials: true,
            }
        )
            .then((res) => {
                setUnits(res.data || []);
            })
            .catch((err) => {
                console.error("Failed to fetch units:", err);
            });

    }, [selectedSubject]);


    // =========================================================
    // LOAD SAVED SKILLS
    // =========================================================

    const loadUnitSkills = async () => {
        if (!selectedSubject || !selectedUnit) {
            setDokLevels([]);
            setSavedSkills([]);
            setSelectedSavedSkillIds([]);
            return;
        }

        try {
            const res = await api.get(
                `${process.env.REACT_APP_BACKEND_URL}/grades/edit-subject-unit-skills/`,
                {
                    params: {
                        subject_id: selectedSubject,
                        unit_id: selectedUnit,
                    },
                    withCredentials: true,
                }
            );

            setDokLevels(res.data?.dok_levels || []);
            setSavedSkills(res.data?.skills || []);
            setSelectedSavedSkillIds([]);

        } catch (err) {
            console.error("Failed to load unit skills:", err);

            setErrorDialog({
                open: true,
                message: "Failed to load skills for this unit.",
            });
        }
    };


    useEffect(() => {
        if (!selectedSubject || !selectedUnit) {
            setSavedSkills([]);
            setDokLevels([]);
            setSelectedSavedSkillIds([]);
            setGeneratedSkills([]);
            setAddingSkillDok(null);
            setEditingSkillId(null);
            return;
        }

        loadUnitSkills();

        setGeneratedSkills([]);
        setSelectedSavedSkillIds([]);
        setAddingSkillDok(null);
        setEditingSkillId(null);

    }, [selectedSubject, selectedUnit]);


    // =========================================================
    // HELPERS
    // =========================================================

    const getDokInfo = (level) => {
        return dokLevels.find(
            (dok) => Number(dok.level) === Number(level)
        );
    };


    const savedSkillsByDok = useMemo(() => {
        const grouped = {
            1: [],
            2: [],
            3: [],
            4: [],
        };

        savedSkills.forEach((skill) => {
            const level = Number(skill.dok_level);

            if (grouped[level]) {
                grouped[level].push(skill);
            }
        });

        Object.keys(grouped).forEach((level) => {
            grouped[level].sort((a, b) => {
                const orderA = Number(a.display_order ?? 0);
                const orderB = Number(b.display_order ?? 0);

                if (orderA !== orderB) {
                    return orderA - orderB;
                }

                return Number(a.id) - Number(b.id);
            });
        });

        return grouped;

    }, [savedSkills]);


    const generatedSkillsByDok = useMemo(() => {
        const grouped = {
            1: [],
            2: [],
            3: [],
            4: [],
        };

        generatedSkills.forEach((skill) => {
            const level = Number(skill.dok_level);

            if (grouped[level]) {
                grouped[level].push(skill);
            }
        });

        return grouped;

    }, [generatedSkills]);


    const selectedGeneratedCount = useMemo(() => {
        return generatedSkills.filter(
            (skill) => skill.selected
        ).length;
    }, [generatedSkills]);


    const allSavedSelected =
        savedSkills.length > 0 &&
        selectedSavedSkillIds.length === savedSkills.length;


    const someSavedSelected =
        selectedSavedSkillIds.length > 0 &&
        selectedSavedSkillIds.length < savedSkills.length;


    // =========================================================
    // DOK COLLAPSE
    // =========================================================

    const toggleDokExpanded = (level) => {
        setExpandedDok((current) => ({
            ...current,
            [level]: !current[level],
        }));
    };


    // =========================================================
    // SAVED SKILL SELECTION
    // =========================================================

    const toggleSavedSkill = (skillId) => {
        setSelectedSavedSkillIds((current) => {
            if (current.includes(skillId)) {
                return current.filter(
                    (id) => id !== skillId
                );
            }

            return [
                ...current,
                skillId,
            ];
        });
    };


    const toggleSelectAllSaved = () => {
        if (allSavedSelected) {
            setSelectedSavedSkillIds([]);
            return;
        }

        setSelectedSavedSkillIds(
            savedSkills.map(
                (skill) => skill.id
            )
        );
    };


    // =========================================================
    // EDIT SAVED SKILL
    // =========================================================

    const startEditingSkill = (skill) => {
        setEditingSkillId(skill.id);
        setEditingSkillText(
            skill.skill_description || ""
        );
    };


    const cancelEditingSkill = () => {
        setEditingSkillId(null);
        setEditingSkillText("");
    };


    const saveEditedSkill = async (skillId) => {
        const description =
            editingSkillText.trim();

        if (!description) {
            setErrorDialog({
                open: true,
                message: "Skill description cannot be blank.",
            });

            return;
        }

        if (
            checkFreeText(
                description,
                "Skill Description"
            ).blocked
        ) {
            return;
        }

        setSavingSkillId(skillId);

        try {
            await api.patch(
                `${process.env.REACT_APP_BACKEND_URL}/grades/edit-subject-unit-skills/`,
                {
                    subject_id: selectedSubject,
                    unit_id: selectedUnit,

                    skills: [
                        {
                            id: skillId,
                            skill_description: description,
                        },
                    ],
                },
                {
                    withCredentials: true,
                }
            );

            await loadUnitSkills();

            setEditingSkillId(null);
            setEditingSkillText("");

        } catch (err) {
            console.error("Failed to update skill:", err);

            setErrorDialog({
                open: true,
                message: "Failed to update the skill.",
            });

        } finally {
            setSavingSkillId(null);
        }
    };


    // =========================================================
    // DELETE SAVED SKILLS
    // =========================================================

    const requestDeleteSingleSkill = (skillId) => {
        setDeleteDialog({
            open: true,
            type: "single",
            skillId,
        });
    };


    const requestDeleteSelected = () => {
        if (selectedSavedSkillIds.length === 0) {
            return;
        }

        setDeleteDialog({
            open: true,
            type: "selected",
            skillId: null,
        });
    };


    const performDelete = async () => {
        const skillIds =
            deleteDialog.type === "single"
                ? [deleteDialog.skillId]
                : selectedSavedSkillIds;

        try {
            await api.delete(
                `${process.env.REACT_APP_BACKEND_URL}/grades/edit-subject-unit-skills/`,
                {
                    data: {
                        subject_id: selectedSubject,
                        unit_id: selectedUnit,
                        skill_ids: skillIds,
                    },

                    withCredentials: true,
                }
            );

            setDeleteDialog({
                open: false,
                type: null,
                skillId: null,
            });

            await loadUnitSkills();

        } catch (err) {
            console.error("Failed to delete skill:", err);

            setErrorDialog({
                open: true,
                message:
                    err?.response?.data?.error ||
                    "Failed to delete the selected skill(s).",
            });
        }
    };


    // =========================================================
    // ADD MANUAL SKILL
    // =========================================================

    const openAddSkill = (level) => {
        setAddingSkillDok(level);
        setNewSkillDescription("");
    };


    const cancelAddSkill = () => {
        setAddingSkillDok(null);
        setNewSkillDescription("");
    };


    const saveNewSkill = async (level) => {
        const description =
            newSkillDescription.trim();

        if (!description) {
            setErrorDialog({
                open: true,
                message: "Please enter a skill description.",
            });

            return;
        }

        if (
            checkFreeText(
                description,
                "Skill Description"
            ).blocked
        ) {
            return;
        }

        const existing =
            savedSkillsByDok[level] || [];

        const existingOrders =
            existing.map(
                (skill) =>
                    Number(skill.display_order ?? 0)
            );

        const nextDisplayOrder =
            existingOrders.length > 0
                ? Math.max(...existingOrders) + 1
                : 0;

        setAddingSkill(true);

        try {
            await api.post(
                `${process.env.REACT_APP_BACKEND_URL}/grades/skills/save-selected/`,
                {
                    subject_id: selectedSubject,
                    unit_id: selectedUnit,

                    skills: [
                        {
                            dok_level: level,
                            skill_description: description,
                            display_order: nextDisplayOrder,
                        },
                    ],
                },
                {
                    withCredentials: true,
                }
            );

            setAddingSkillDok(null);
            setNewSkillDescription("");

            await loadUnitSkills();

        } catch (err) {
            console.error("Failed to add skill:", err);

            setErrorDialog({
                open: true,
                message:
                    err?.response?.data?.error ||
                    "Failed to add the skill.",
            });

        } finally {
            setAddingSkill(false);
        }
    };


    // =========================================================
    // AI MODAL
    // =========================================================

    const openAiGenerator = () => {
        setAiDialogOpen(true);

        if (generatedSkills.length === 0) {
            setActiveAiDok(1);
        }
    };


    const closeAiGenerator = () => {
        if (generating || savingGenerated) {
            return;
        }

        setAiDialogOpen(false);
        setEditingGeneratedId(null);
        setEditingGeneratedText("");
    };


    const validatePrompt = (text) =>
        text.trim().length > 5 &&
        /^[\w\s.,'":;!?()\-\/]+$/.test(text);


    const createTempId = (level, index) => {
        return (
            `dok-${level}-` +
            `${Date.now()}-` +
            `${index}-` +
            `${Math.random().toString(36).substring(2, 8)}`
        );
    };


    const parseGeneratedSkills = (aiData) => {
        const candidates = [];

        [1, 2, 3, 4].forEach((level) => {
            const skills =
                aiData?.[String(level)] ||
                aiData?.[level] ||
                [];

            if (!Array.isArray(skills)) {
                return;
            }

            skills.forEach(
                (description, index) => {
                    candidates.push({
                        temp_id:
                            createTempId(
                                level,
                                index
                            ),

                        dok_level: level,

                        skill_description:
                            String(description),

                        display_order: index,

                        selected: true,
                    });
                }
            );
        });

        return candidates;
    };


    // =========================================================
    // GENERATE ALL AI SKILLS
    // =========================================================

    const generateAllAiSkills = async () => {
        if (!validatePrompt(topicPrompt)) {
            setErrorDialog({
                open: true,
                message:
                    "Please enter a valid topic or learning objective.",
            });

            return;
        }

        if (
            checkFreeText(
                topicPrompt,
                "Topic/Prompt"
            ).blocked
        ) {
            return;
        }

        setGenerating(true);

        try {
            const res = await api.post(
                `${process.env.REACT_APP_BACKEND_URL}/grades/progress-card/generate-dok-skills/`,
                {
                    prompt: topicPrompt,
                },
                {
                    withCredentials: true,
                }
            );

            const candidates =
                parseGeneratedSkills(
                    res.data || {}
                );

            if (candidates.length === 0) {
                throw new Error(
                    "No skills were generated."
                );
            }

            // Replace temporary AI suggestions only
            setGeneratedSkills(candidates);
            setActiveAiDok(1);

        } catch (err) {
            handleAIError(
                err,
                {
                    setErrorMsg: (msg) =>
                        setErrorDialog({
                            open: true,
                            message: msg,
                        }),

                    setShowLimitDialog,
                }
            );

        } finally {
            setGenerating(false);
        }
    };


    // =========================================================
    // REGENERATE ONE DOK
    // =========================================================

    const regenerateDok = async (level) => {
        if (!validatePrompt(topicPrompt)) {
            setErrorDialog({
                open: true,
                message:
                    "Please enter a valid topic or learning objective.",
            });

            return;
        }

        if (
            checkFreeText(
                topicPrompt,
                "Topic/Prompt"
            ).blocked
        ) {
            return;
        }

        setRegeneratingDok(level);

        try {
            const res = await api.post(
                `${process.env.REACT_APP_BACKEND_URL}/grades/progress-card/generate-dok-skills/`,
                {
                    prompt: topicPrompt,
                },
                {
                    withCredentials: true,
                }
            );

            const allNewCandidates =
                parseGeneratedSkills(
                    res.data || {}
                );

            const newLevelCandidates =
                allNewCandidates.filter(
                    (skill) =>
                        Number(skill.dok_level) ===
                        Number(level)
                );

            if (newLevelCandidates.length === 0) {
                throw new Error(
                    `No DOK ${level} skills were generated.`
                );
            }

            // Keep other DOK candidates unchanged
            setGeneratedSkills((current) => [
                ...current.filter(
                    (skill) =>
                        Number(skill.dok_level) !==
                        Number(level)
                ),
                ...newLevelCandidates,
            ]);

        } catch (err) {
            handleAIError(
                err,
                {
                    setErrorMsg: (msg) =>
                        setErrorDialog({
                            open: true,
                            message: msg,
                        }),

                    setShowLimitDialog,
                }
            );

        } finally {
            setRegeneratingDok(null);
        }
    };


    // =========================================================
    // GENERATED SKILL SELECTION
    // =========================================================

    const toggleGeneratedSkill = (tempId) => {
        setGeneratedSkills((current) =>
            current.map((skill) =>
                skill.temp_id === tempId
                    ? {
                        ...skill,
                        selected: !skill.selected,
                    }
                    : skill
            )
        );
    };


    const toggleAllGeneratedForDok = (level) => {
        const levelSkills =
            generatedSkillsByDok[level] || [];

        if (levelSkills.length === 0) {
            return;
        }

        const allSelected =
            levelSkills.every(
                (skill) => skill.selected
            );

        setGeneratedSkills((current) =>
            current.map((skill) =>
                Number(skill.dok_level) ===
                Number(level)
                    ? {
                        ...skill,
                        selected: !allSelected,
                    }
                    : skill
            )
        );
    };


    // =========================================================
    // EDIT GENERATED CANDIDATE
    // =========================================================

    const startEditingGenerated = (skill) => {
        setEditingGeneratedId(skill.temp_id);
        setEditingGeneratedText(
            skill.skill_description
        );
    };


    const cancelEditingGenerated = () => {
        setEditingGeneratedId(null);
        setEditingGeneratedText("");
    };


    const saveGeneratedEdit = (tempId) => {
        const text =
            editingGeneratedText.trim();

        if (!text) {
            setErrorDialog({
                open: true,
                message:
                    "Generated skill description cannot be blank.",
            });

            return;
        }

        if (
            checkFreeText(
                text,
                "Skill Description"
            ).blocked
        ) {
            return;
        }

        setGeneratedSkills((current) =>
            current.map((skill) =>
                skill.temp_id === tempId
                    ? {
                        ...skill,
                        skill_description: text,
                    }
                    : skill
            )
        );

        setEditingGeneratedId(null);
        setEditingGeneratedText("");
    };


    // =========================================================
    // ADD AI SKILLS TO UNIT
    // =========================================================

    const addSelectedGeneratedSkills = async () => {
        const selected =
            generatedSkills.filter(
                (skill) => skill.selected
            );

        if (selected.length === 0) {
            setErrorDialog({
                open: true,
                message:
                    "Select at least one AI-generated skill.",
            });

            return;
        }

        for (const skill of selected) {
            const description =
                skill.skill_description.trim();

            if (!description) {
                setErrorDialog({
                    open: true,
                    message:
                        "Selected skills cannot have blank descriptions.",
                });

                return;
            }

            if (
                checkFreeText(
                    description,
                    "Skill Description"
                ).blocked
            ) {
                return;
            }
        }

        const payload = [];

        [1, 2, 3, 4].forEach((level) => {
            const selectedForLevel =
                selected.filter(
                    (skill) =>
                        Number(skill.dok_level) ===
                        level
                );

            const existingForLevel =
                savedSkillsByDok[level] || [];

            const existingOrders =
                existingForLevel.map(
                    (skill) =>
                        Number(
                            skill.display_order ?? 0
                        )
                );

            const startingOrder =
                existingOrders.length > 0
                    ? Math.max(
                        ...existingOrders
                    ) + 1
                    : 0;

            selectedForLevel.forEach(
                (skill, index) => {
                    payload.push({
                        dok_level: level,

                        skill_description:
                            skill.skill_description.trim(),

                        display_order:
                            startingOrder + index,
                    });
                }
            );
        });

        setSavingGenerated(true);

        try {
            await api.post(
                `${process.env.REACT_APP_BACKEND_URL}/grades/skills/save-selected/`,
                {
                    subject_id: selectedSubject,
                    unit_id: selectedUnit,
                    skills: payload,
                },
                {
                    withCredentials: true,
                }
            );

            const count = payload.length;

            await loadUnitSkills();

            setGeneratedSkills([]);
            setAiDialogOpen(false);
            setEditingGeneratedId(null);

            setSuccessDialog({
                open: true,
                title:
                    "Skills Added to Unit",

                message:
                    `${count} skill` +
                    `${count === 1 ? "" : "s"} ` +
                    "added successfully.",
            });

        } catch (err) {
            console.error(
                "Failed to save generated skills:",
                err
            );

            setErrorDialog({
                open: true,
                message:
                    err?.response?.data?.error ||
                    "Failed to add selected skills.",
            });

        } finally {
            setSavingGenerated(false);
        }
    };


    // =========================================================
    // ACTIVE AI DOK
    // =========================================================

    const activeGeneratedSkills =
        generatedSkillsByDok[activeAiDok] || [];


    const activeGeneratedSelectedCount =
        activeGeneratedSkills.filter(
            (skill) => skill.selected
        ).length;


    const allActiveGeneratedSelected =
        activeGeneratedSkills.length > 0 &&
        activeGeneratedSkills.every(
            (skill) => skill.selected
        );


    const someActiveGeneratedSelected =
        activeGeneratedSelectedCount > 0 &&
        activeGeneratedSelectedCount <
            activeGeneratedSkills.length;


    // =========================================================
    // RENDER
    // =========================================================

    return (
        <Box
            sx={{
                p: {
                    xs: 2,
                    sm: 4,
                },

                maxWidth: 1100,
                mx: "auto",
                borderRadius: 3,
            }}
        >

            {/* =================================================
                PAGE HEADER
            ================================================= */}

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    mb: 1,
                }}
            >
                <Box
                    sx={{
                        width: 6,
                        height: 36,
                        bgcolor: "primary.main",
                        borderRadius: 1,
                    }}
                />

                <Typography
                    variant="h6"
                    fontWeight={700}
                >
                    Manage Unit Skills
                </Typography>
            </Box>

            <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                    ml: 2.5,
                    mb: 3,
                }}
            >
                Add, edit, delete, or generate individual
                DOK learning targets for each unit.
            </Typography>


            {/* =================================================
                SUBJECT + UNIT
            ================================================= */}

            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 3,
                }}
            >
                <Grid
                    container
                    spacing={2}
                >
                    <Grid
                        item
                        xs={12}
                        md={6}
                    >
                        <Typography
                            fontWeight="bold"
                            mb={1}
                        >
                            Select Subject
                        </Typography>

                        <Select
                            fullWidth
                            displayEmpty
                            value={selectedSubject}
                            onChange={(e) => {
                                setSelectedSubject(
                                    e.target.value
                                );

                                setSelectedUnit("");
                                setGeneratedSkills([]);
                                setTopicPrompt("");
                            }}
                            sx={{
                                borderRadius: 999,
                            }}
                        >
                            <MenuItem value="">
                                -- Select Subject --
                            </MenuItem>

                            {subjects.map(
                                (subject) => (
                                    <MenuItem
                                        key={subject.id}
                                        value={subject.id}
                                    >
                                        {subject.subject_name}{" "}
                                        {subject.class_level}
                                    </MenuItem>
                                )
                            )}
                        </Select>
                    </Grid>


                    <Grid
                        item
                        xs={12}
                        md={6}
                    >
                        <Typography
                            fontWeight="bold"
                            mb={1}
                        >
                            Select Unit
                        </Typography>

                        <Select
                            fullWidth
                            displayEmpty
                            value={selectedUnit}
                            disabled={!selectedSubject}
                            onChange={(e) => {
                                setSelectedUnit(
                                    e.target.value
                                );

                                setGeneratedSkills([]);
                            }}
                            sx={{
                                borderRadius: 999,
                            }}
                        >
                            <MenuItem value="">
                                -- Select Unit --
                            </MenuItem>

                            {units.map(
                                (unit) => (
                                    <MenuItem
                                        key={unit.id}
                                        value={unit.id}
                                    >
                                        {unit.unit_name}
                                    </MenuItem>
                                )
                            )}
                        </Select>
                    </Grid>
                </Grid>
            </Paper>


            {/* =================================================
                SAVED SKILLS WORKSPACE
            ================================================= */}

            {selectedUnit && (
                <Paper
                    elevation={0}
                    sx={{
                        p: {
                            xs: 2,
                            sm: 3,
                        },

                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 3,
                    }}
                >

                    {/* Header */}

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={2}
                    >
                        <Box>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                            >
                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                >
                                    Saved Skills
                                </Typography>

                                <Chip
                                    size="small"
                                    label={`${savedSkills.length} total`}
                                />
                            </Box>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Skills currently available for
                                this unit.
                            </Typography>
                        </Box>


                        <Button
                            variant="contained"
                            startIcon={
                                <AutoAwesomeIcon />
                            }
                            onClick={
                                openAiGenerator
                            }
                            sx={{
                                textTransform: "none",
                                fontWeight: 700,
                                px: 2.5,
                                py: 1,
                                borderRadius: 2,
                                color: "#fff",
                                backgroundImage:
                                    "linear-gradient(135deg, #8AA352 0%, #647A33 100%)",
                                boxShadow:
                                    "0 3px 10px rgba(100, 122, 51, 0.35)",
                                "&:hover": {
                                    backgroundImage:
                                        "linear-gradient(135deg, #7C9548 0%, #556B2F 100%)",
                                    boxShadow:
                                        "0 4px 12px rgba(100, 122, 51, 0.45)",
                                },
                            }}
                        >
                            Generate with AI
                        </Button>
                    </Box>


                    <Divider
                        sx={{
                            my: 2,
                        }}
                    />


                    {/* Selection Toolbar */}

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1}
                        sx={{
                            minHeight: 46,
                            mb: 2,
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={
                                        allSavedSelected
                                    }
                                    indeterminate={
                                        someSavedSelected
                                    }
                                    onChange={
                                        toggleSelectAllSaved
                                    }
                                />
                            }
                            label="Select All"
                        />


                        {selectedSavedSkillIds.length > 0 && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    px: 1.5,
                                    py: 0.75,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    borderRadius: 2,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                >
                                    {
                                        selectedSavedSkillIds.length
                                    }{" "}
                                    selected
                                </Typography>

                                <Button
                                    size="small"
                                    color="error"
                                    startIcon={
                                        <DeleteIcon />
                                    }
                                    onClick={
                                        requestDeleteSelected
                                    }
                                >
                                    Delete Selected
                                </Button>
                            </Paper>
                        )}
                    </Box>


                    {/* =================================================
                        DOK SECTIONS
                    ================================================= */}

                    {[1, 2, 3, 4].map(
                        (level) => {
                            const levelSkills =
                                savedSkillsByDok[level] ||
                                [];

                            const dokInfo =
                                getDokInfo(level);

                            const dokStyle =
                                DOK_LEVEL_STYLES[level];

                            return (
                                <Paper
                                    key={level}
                                    variant="outlined"
                                    sx={{
                                        mb: 2,
                                        borderRadius: 3,
                                        overflow: "hidden",
                                        borderLeft: "4px solid",
                                        borderLeftColor:
                                            dokStyle.border,
                                    }}
                                >

                                    {/* DOK HEADER */}

                                    <Box
                                        onClick={() =>
                                            toggleDokExpanded(
                                                level
                                            )
                                        }
                                        sx={{
                                            px: 2,
                                            py: 1.5,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent:
                                                "space-between",
                                            cursor: "pointer",
                                            backgroundColor:
                                                dokStyle.background,

                                            "&:hover": {
                                                bgcolor:
                                                    "action.hover",
                                            },
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            flexWrap="wrap"
                                        >
                                            <Chip
                                                size="small"
                                                label={`DOK ${level}`}
                                                sx={{
                                                    fontWeight: 700,
                                                    color: "#fff",
                                                    backgroundColor:
                                                        dokStyle.color,
                                                }}
                                            />

                                            <Typography
                                                fontWeight="bold"
                                            >
                                                {
                                                    dokInfo?.description
                                                }
                                            </Typography>

                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={`${levelSkills.length} skill${
                                                    levelSkills.length ===
                                                    1
                                                        ? ""
                                                        : "s"
                                                }`}
                                            />
                                        </Box>

                                        {expandedDok[level] ? (
                                            <ExpandLess />
                                        ) : (
                                            <ExpandMore />
                                        )}
                                    </Box>


                                    {/* DOK CONTENT */}

                                    <Collapse
                                        in={
                                            expandedDok[
                                                level
                                            ]
                                        }
                                    >
                                        <Divider />

                                        <Box
                                            sx={{
                                                p: 2,
                                            }}
                                        >

                                            {levelSkills.length ===
                                            0 ? (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        mb: 2,
                                                    }}
                                                >
                                                    No skills
                                                    have been
                                                    added for
                                                    this DOK
                                                    level.
                                                </Typography>
                                            ) : (
                                                <Box>
                                                    {levelSkills.map(
                                                        (
                                                            skill,
                                                            skillIndex
                                                        ) => {
                                                            const isSelected =
                                                                selectedSavedSkillIds.includes(
                                                                    skill.id
                                                                );

                                                            const isEditing =
                                                                editingSkillId ===
                                                                skill.id;

                                                            return (
                                                                <Box
                                                                    key={
                                                                        skill.id
                                                                    }
                                                                    sx={{
                                                                        py: 2,

                                                                        borderBottom:
                                                                            "1px solid",

                                                                        borderColor:
                                                                            "divider",

                                                                        "&:last-of-type":
                                                                            {
                                                                                borderBottom:
                                                                                    "none",
                                                                            },
                                                                    }}
                                                                >
                                                                    <Box
                                                                        display="flex"
                                                                        alignItems="flex-start"
                                                                        gap={
                                                                            1
                                                                        }
                                                                    >
                                                                        <Checkbox
                                                                            checked={
                                                                                isSelected
                                                                            }
                                                                            onChange={() =>
                                                                                toggleSavedSkill(
                                                                                    skill.id
                                                                                )
                                                                            }
                                                                            sx={{
                                                                                mt: -0.5,
                                                                            }}
                                                                        />

                                                                        <Box
                                                                            flex={
                                                                                1
                                                                            }
                                                                            minWidth={
                                                                                0
                                                                            }
                                                                        >
                                                                            {isEditing ? (
                                                                                <>
                                                                                    <TextField
                                                                                        fullWidth
                                                                                        multiline
                                                                                        minRows={
                                                                                            2
                                                                                        }
                                                                                        maxRows={
                                                                                            6
                                                                                        }
                                                                                        value={
                                                                                            editingSkillText
                                                                                        }
                                                                                        onChange={(
                                                                                            e
                                                                                        ) =>
                                                                                            setEditingSkillText(
                                                                                                e
                                                                                                    .target
                                                                                                    .value
                                                                                            )
                                                                                        }
                                                                                        sx={{
                                                                                            "& .MuiOutlinedInput-root":
                                                                                                {
                                                                                                    borderRadius: 2.5,
                                                                                                },
                                                                                        }}
                                                                                    />

                                                                                    <Box
                                                                                        display="flex"
                                                                                        justifyContent="flex-end"
                                                                                        gap={
                                                                                            1
                                                                                        }
                                                                                        mt={
                                                                                            1
                                                                                        }
                                                                                    >
                                                                                        <Button
                                                                                            size="small"
                                                                                            onClick={
                                                                                                cancelEditingSkill
                                                                                            }
                                                                                        >
                                                                                            Cancel
                                                                                        </Button>

                                                                                        <Button
                                                                                            size="small"
                                                                                            variant="contained"
                                                                                            startIcon={
                                                                                                <SaveIcon />
                                                                                            }
                                                                                            disabled={
                                                                                                savingSkillId ===
                                                                                                skill.id
                                                                                            }
                                                                                            onClick={() =>
                                                                                                saveEditedSkill(
                                                                                                    skill.id
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            {savingSkillId ===
                                                                                            skill.id
                                                                                                ? "Saving..."
                                                                                                : "Save"}
                                                                                        </Button>
                                                                                    </Box>
                                                                                </>
                                                                            ) : (
                                                                                <Box
                                                                                    display="flex"
                                                                                    justifyContent="space-between"
                                                                                    alignItems="flex-start"
                                                                                    gap={
                                                                                        2
                                                                                    }
                                                                                >
                                                                                    <Box
                                                                                        minWidth={
                                                                                            0
                                                                                        }
                                                                                    >
                                                                                        <Chip
                                                                                            size="small"
                                                                                            label={`#${
                                                                                                skillIndex +
                                                                                                1
                                                                                            }`}
                                                                                            sx={{
                                                                                                height: 20,
                                                                                                fontSize:
                                                                                                    "0.7rem",
                                                                                                fontWeight: 700,
                                                                                                color: dokStyle.color,
                                                                                                backgroundColor:
                                                                                                    dokStyle.background,
                                                                                                border: "1px solid",
                                                                                                borderColor:
                                                                                                    dokStyle.border,
                                                                                            }}
                                                                                        />

                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            sx={{
                                                                                                mt: 0.75,
                                                                                                lineHeight: 1.6,
                                                                                            }}
                                                                                        >
                                                                                            {
                                                                                                skill.skill_description
                                                                                            }
                                                                                        </Typography>
                                                                                    </Box>

                                                                                    <Box
                                                                                        display="flex"
                                                                                        flexShrink={
                                                                                            0
                                                                                        }
                                                                                    >
                                                                                        <Tooltip title="Edit skill">
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                onClick={() =>
                                                                                                    startEditingSkill(
                                                                                                        skill
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                <EditIcon fontSize="small" />
                                                                                            </IconButton>
                                                                                        </Tooltip>

                                                                                        <Tooltip title="Delete skill">
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                color="error"
                                                                                                onClick={() =>
                                                                                                    requestDeleteSingleSkill(
                                                                                                        skill.id
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                <DeleteIcon fontSize="small" />
                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                    </Box>
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            );
                                                        }
                                                    )}
                                                </Box>
                                            )}


                                            {/* ADD SKILL */}

                                            {addingSkillDok ===
                                            level ? (
                                                <Paper
                                                    variant="outlined"
                                                    sx={{
                                                        mt: 2,
                                                        p: 2,
                                                        borderRadius: 2,
                                                        borderStyle:
                                                            "dashed",
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle2"
                                                        fontWeight="bold"
                                                        mb={1}
                                                    >
                                                        New DOK{" "}
                                                        {level}{" "}
                                                        Skill
                                                    </Typography>

                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        minRows={
                                                            2
                                                        }
                                                        placeholder="Student can..."
                                                        value={
                                                            newSkillDescription
                                                        }
                                                        onChange={(
                                                            e
                                                        ) =>
                                                            setNewSkillDescription(
                                                                e
                                                                    .target
                                                                    .value
                                                            )
                                                        }
                                                        sx={{
                                                            "& .MuiOutlinedInput-root":
                                                                {
                                                                    borderRadius: 2.5,
                                                                },
                                                        }}
                                                    />

                                                    <Box
                                                        display="flex"
                                                        justifyContent="flex-end"
                                                        gap={
                                                            1
                                                        }
                                                        mt={
                                                            1.5
                                                        }
                                                    >
                                                        <Button
                                                            onClick={
                                                                cancelAddSkill
                                                            }
                                                        >
                                                            Cancel
                                                        </Button>

                                                        <Button
                                                            variant="contained"
                                                            startIcon={
                                                                <SaveIcon />
                                                            }
                                                            disabled={
                                                                addingSkill
                                                            }
                                                            onClick={() =>
                                                                saveNewSkill(
                                                                    level
                                                                )
                                                            }
                                                        >
                                                            {addingSkill
                                                                ? "Saving..."
                                                                : "Save Skill"}
                                                        </Button>
                                                    </Box>
                                                </Paper>
                                            ) : (
                                                <Button
                                                    size="small"
                                                    startIcon={
                                                        <AddIcon />
                                                    }
                                                    sx={{
                                                        mt: 2,
                                                    }}
                                                    onClick={() =>
                                                        openAddSkill(
                                                            level
                                                        )
                                                    }
                                                >
                                                    Add DOK{" "}
                                                    {level}{" "}
                                                    Skill
                                                </Button>
                                            )}
                                        </Box>
                                    </Collapse>
                                </Paper>
                            );
                        }
                    )}
                </Paper>
            )}


            {/* =================================================
                AI GENERATOR MODAL
            ================================================= */}

            <Dialog
                open={aiDialogOpen}
                onClose={closeAiGenerator}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        maxHeight: "90vh",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        pb: 1,
                    }}
                >
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap={2}
                    >
                        <Box>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                            >
                                <AutoAwesomeIcon
                                    color="secondary"
                                />

                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                >
                                    Generate Skills
                                    with AI
                                </Typography>
                            </Box>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    mt: 0.5,
                                }}
                            >
                                Generate, review,
                                edit, and select
                                learning targets before
                                adding them to this unit.
                            </Typography>
                        </Box>

                        <IconButton
                            onClick={
                                closeAiGenerator
                            }
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>


                <DialogContent dividers>

                    {/* Prompt */}

                    <Box
                        sx={{
                            mb: 3,
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={1}
                        >
                            <Typography
                                fontWeight="bold"
                            >
                                Topic or Learning
                                Objective
                            </Typography>

                            <PromptHelpDialog
                                title="How to Write a Good Topic Description"
                                content={
                                    helperForSkills
                                }
                            />
                        </Box>

                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={5}
                            value={topicPrompt}
                            onChange={(e) =>
                                setTopicPrompt(
                                    e.target.value
                                )
                            }
                            placeholder="e.g. Multiplication for Grade 1 using equal groups and repeated addition"
                        />

                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            gap={1}
                            mt={1.5}
                        >
                            {generatedSkills.length >
                                0 && (
                                <Button
                                    variant="outlined"
                                    startIcon={
                                        <AutoAwesomeIcon />
                                    }
                                    disabled={
                                        generating
                                    }
                                    onClick={
                                        generateAllAiSkills
                                    }
                                >
                                    Regenerate All
                                </Button>
                            )}

                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={
                                    generating ? null : (
                                        <PsychologyIcon />
                                    )
                                }
                                disabled={
                                    generating
                                }
                                onClick={
                                    generateAllAiSkills
                                }
                            >
                                {generating ? (
                                    <>
                                        <CircularProgress
                                            size={18}
                                            sx={{
                                                mr: 1,
                                            }}
                                        />
                                        Generating...
                                    </>
                                ) : generatedSkills.length >
                                  0 ? (
                                    "Generate Again"
                                ) : (
                                    "Generate Skills"
                                )}
                            </Button>
                        </Box>
                    </Box>


                    {/* Generated Content */}

                    {generatedSkills.length >
                    0 ? (
                        <>
                            <Divider />


                            {/* DOK Tabs */}

                            <Tabs
                                value={
                                    activeAiDok
                                }
                                onChange={(
                                    _,
                                    value
                                ) =>
                                    setActiveAiDok(
                                        value
                                    )
                                }
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{
                                    mt: 1,
                                    mb: 2,
                                }}
                            >
                                {[1, 2, 3, 4].map(
                                    (level) => {
                                        const skills =
                                            generatedSkillsByDok[
                                                level
                                            ] || [];

                                        const selectedCount =
                                            skills.filter(
                                                (
                                                    skill
                                                ) =>
                                                    skill.selected
                                            ).length;

                                        return (
                                            <Tab
                                                key={
                                                    level
                                                }
                                                value={
                                                    level
                                                }
                                                label={
                                                    `DOK ${level} · ` +
                                                    `${selectedCount}/${skills.length}`
                                                }
                                            />
                                        );
                                    }
                                )}
                            </Tabs>


                            {/* Active DOK Header */}

                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                flexWrap="wrap"
                                gap={1}
                                mb={2}
                            >
                                <Box>
                                    <Typography
                                        fontWeight="bold"
                                    >
                                        DOK{" "}
                                        {
                                            activeAiDok
                                        }{" "}
                                        ·{" "}
                                        {
                                            getDokInfo(
                                                activeAiDok
                                            )
                                                ?.description
                                        }
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {
                                            activeGeneratedSelectedCount
                                        }{" "}
                                        of{" "}
                                        {
                                            activeGeneratedSkills.length
                                        }{" "}
                                        selected
                                    </Typography>
                                </Box>

                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    flexWrap="wrap"
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={
                                                    allActiveGeneratedSelected
                                                }
                                                indeterminate={
                                                    someActiveGeneratedSelected
                                                }
                                                onChange={() =>
                                                    toggleAllGeneratedForDok(
                                                        activeAiDok
                                                    )
                                                }
                                            />
                                        }
                                        label="Select All"
                                    />

                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={
                                            regeneratingDok ===
                                            activeAiDok ? null : (
                                                <AutoAwesomeIcon />
                                            )
                                        }
                                        disabled={
                                            regeneratingDok !==
                                            null
                                        }
                                        onClick={() =>
                                            regenerateDok(
                                                activeAiDok
                                            )
                                        }
                                    >
                                        {regeneratingDok ===
                                        activeAiDok ? (
                                            <>
                                                <CircularProgress
                                                    size={
                                                        16
                                                    }
                                                    sx={{
                                                        mr: 1,
                                                    }}
                                                />
                                                Regenerating...
                                            </>
                                        ) : (
                                            `Regenerate DOK ${activeAiDok}`
                                        )}
                                    </Button>
                                </Box>
                            </Box>


                            {/* Candidate Rows */}

                            <Box>
                                {activeGeneratedSkills.map(
                                    (
                                        skill,
                                        index
                                    ) => {
                                        const isEditing =
                                            editingGeneratedId ===
                                            skill.temp_id;

                                        return (
                                            <Paper
                                                key={
                                                    skill.temp_id
                                                }
                                                variant="outlined"
                                                sx={{
                                                    p: 1.5,
                                                    mb: 1.25,
                                                    borderRadius: 2,

                                                    borderColor:
                                                        skill.selected
                                                            ? "success.main"
                                                            : "divider",

                                                    bgcolor:
                                                        skill.selected
                                                            ? "action.selected"
                                                            : "background.paper",
                                                }}
                                            >
                                                <Box
                                                    display="flex"
                                                    alignItems="flex-start"
                                                    gap={
                                                        1
                                                    }
                                                >
                                                    <Checkbox
                                                        checked={
                                                            skill.selected
                                                        }
                                                        onChange={() =>
                                                            toggleGeneratedSkill(
                                                                skill.temp_id
                                                            )
                                                        }
                                                        sx={{
                                                            mt: -0.5,
                                                        }}
                                                    />

                                                    <Box
                                                        flex={
                                                            1
                                                        }
                                                        minWidth={
                                                            0
                                                        }
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Suggestion{" "}
                                                            {index +
                                                                1}
                                                        </Typography>

                                                        {isEditing ? (
                                                            <>
                                                                <TextField
                                                                    fullWidth
                                                                    multiline
                                                                    minRows={
                                                                        2
                                                                    }
                                                                    sx={{
                                                                        mt: 0.5,
                                                                    }}
                                                                    value={
                                                                        editingGeneratedText
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        setEditingGeneratedText(
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                />

                                                                <Box
                                                                    display="flex"
                                                                    justifyContent="flex-end"
                                                                    gap={
                                                                        1
                                                                    }
                                                                    mt={
                                                                        1
                                                                    }
                                                                >
                                                                    <Button
                                                                        size="small"
                                                                        onClick={
                                                                            cancelEditingGenerated
                                                                        }
                                                                    >
                                                                        Cancel
                                                                    </Button>

                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        onClick={() =>
                                                                            saveGeneratedEdit(
                                                                                skill.temp_id
                                                                            )
                                                                        }
                                                                    >
                                                                        Apply
                                                                    </Button>
                                                                </Box>
                                                            </>
                                                        ) : (
                                                            <Box
                                                                display="flex"
                                                                justifyContent="space-between"
                                                                alignItems="flex-start"
                                                                gap={
                                                                    1
                                                                }
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        mt: 0.25,
                                                                        lineHeight: 1.6,
                                                                    }}
                                                                >
                                                                    {
                                                                        skill.skill_description
                                                                    }
                                                                </Typography>

                                                                <Tooltip title="Edit suggestion">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() =>
                                                                            startEditingGenerated(
                                                                                skill
                                                                            )
                                                                        }
                                                                    >
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Paper>
                                        );
                                    }
                                )}
                            </Box>
                        </>
                    ) : (
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 4,
                                textAlign: "center",
                                borderRadius: 3,
                                borderStyle:
                                    "dashed",
                            }}
                        >
                            <PsychologyIcon
                                sx={{
                                    fontSize: 46,
                                    color:
                                        "text.disabled",
                                    mb: 1,
                                }}
                            />

                            <Typography
                                fontWeight="bold"
                            >
                                No AI suggestions
                                generated yet
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    mt: 0.5,
                                }}
                            >
                                Enter a topic above and
                                select Generate Skills.
                            </Typography>
                        </Paper>
                    )}
                </DialogContent>


                {/* AI Footer */}

                <DialogActions
                    sx={{
                        px: 3,
                        py: 2,
                        justifyContent:
                            "space-between",
                    }}
                >
                    <Typography
                        variant="body2"
                        fontWeight="bold"
                    >
                        {generatedSkills.length >
                        0
                            ? `${selectedGeneratedCount} skill${
                                  selectedGeneratedCount ===
                                  1
                                      ? ""
                                      : "s"
                              } selected`
                            : ""}
                    </Typography>

                    <Box
                        display="flex"
                        gap={1}
                    >
                        <Button
                            onClick={
                                closeAiGenerator
                            }
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={
                                <AddIcon />
                            }
                            disabled={
                                savingGenerated ||
                                selectedGeneratedCount ===
                                    0
                            }
                            onClick={
                                addSelectedGeneratedSkills
                            }
                        >
                            {savingGenerated
                                ? "Adding..."
                                : `Add Selected to Unit${
                                      selectedGeneratedCount >
                                      0
                                          ? ` (${selectedGeneratedCount})`
                                          : ""
                                  }`}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>


            {/* =================================================
                DELETE CONFIRMATION
            ================================================= */}

            <Dialog
                open={deleteDialog.open}
                onClose={() =>
                    setDeleteDialog({
                        open: false,
                        type: null,
                        skillId: null,
                    })
                }
            >
                <DialogTitle>
                    Delete Skill
                    {deleteDialog.type ===
                    "selected"
                        ? "s"
                        : ""}
                    ?
                </DialogTitle>

                <DialogContent>
                    <Typography>
                        {deleteDialog.type ===
                        "selected"
                            ? `Delete ${selectedSavedSkillIds.length} selected skills?`
                            : "Delete this skill?"}
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mt: 1,
                        }}
                    >
                        This action cannot be undone.
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() =>
                            setDeleteDialog({
                                open: false,
                                type: null,
                                skillId:
                                    null,
                            })
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        color="error"
                        variant="contained"
                        onClick={
                            performDelete
                        }
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>


            {/* =================================================
                SUCCESS DIALOG
            ================================================= */}

            <Dialog
                open={successDialog.open}
                onClose={() =>
                    setSuccessDialog({
                        open: false,
                        title: "",
                        message: "",
                    })
                }
            >
                <DialogTitle
                    sx={{
                        textAlign: "center",
                    }}
                >
                    <CheckCircleIcon
                        sx={{
                            fontSize: 48,
                            color: "success.main",
                        }}
                    />

                    <Typography
                        variant="h6"
                        fontWeight="bold"
                    >
                        {successDialog.title}
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    <Typography
                        textAlign="center"
                        color="text.secondary"
                    >
                        {successDialog.message}
                    </Typography>
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent: "center",
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={() =>
                            setSuccessDialog({
                                open: false,
                                title: "",
                                message: "",
                            })
                        }
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>


            {/* =================================================
                ERROR DIALOG
            ================================================= */}

            <Dialog
                open={errorDialog.open}
                onClose={() =>
                    setErrorDialog({
                        open: false,
                        message: "",
                    })
                }
            >
                <DialogTitle
                    sx={{
                        textAlign: "center",
                    }}
                >
                    <ErrorOutlineIcon
                        sx={{
                            fontSize: 48,
                            color: "error.main",
                        }}
                    />

                    <Typography
                        variant="h6"
                        fontWeight="bold"
                    >
                        Attention Required
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    <Typography>
                        {errorDialog.message}
                    </Typography>
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent: "center",
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={() =>
                            setErrorDialog({
                                open: false,
                                message: "",
                            })
                        }
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>


            <WarningDialog />


            <AIGenerationLimitDialog
                open={showLimitDialog}
                onClose={() =>
                    setShowLimitDialog(false)
                }
                featureName="DOK Skill Generator"
                goToUpgrade={() =>
                    setActiveTabGlobal(
                        "Upgrade"
                    )
                }
            />
        </Box>
    );
};


export default ManageSkillsTab;